// src/pages/finance-portal/inventory/LandedCosts.js
//
// Landed Cost list + create + allocate across receipt lines.

import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Autocomplete, Box, Button, Checkbox, Chip, Dialog, DialogActions,
    DialogContent, DialogTitle, FormControl, Grid, IconButton, InputAdornment,
    InputLabel, MenuItem, Paper, Select, Skeleton, Stack,
    Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import axiosInstance from '../../../utils/axiosInstance';
import {
    FONT_FAMILY, INV_ACCENT, INV_ACCENT_DARK, formatCurrency, formatDate,
} from './components/inventoryFormatters';

const COST_TYPES = ['FREIGHT', 'DUTY', 'CUSTOMS_BROKERAGE', 'INSURANCE', 'HANDLING', 'STORAGE', 'OTHER'];
const BASES = ['VALUE', 'WEIGHT', 'QUANTITY', 'CUSTOM'];
const STATUS_COLORS = {
    DRAFT: { bg: '#fef3c7', color: '#92400e' },
    APPLIED: { bg: '#dcfce7', color: '#166534' },
    VOID: { bg: '#e5e7eb', color: '#374151' },
};

export default function LandedCosts() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('');

    const [vendors, setVendors] = useState([]);
    const [receipts, setReceipts] = useState([]);
    const [createOpen, setCreateOpen] = useState(false);
    const [form, setForm] = useState({
        reference: '', cost_type: 'FREIGHT', vendor_id: null,
        bill_date: new Date().toISOString().slice(0, 10),
        amount: 0, currency: 'USD',
        allocation_basis: 'VALUE', notes: '',
    });
    const [saving, setSaving] = useState(false);

    const [detail, setDetail] = useState(null);
    const [allocate, setAllocate] = useState({ open: false, receipt_line_ids: [] });
    const [error, setError] = useState(null);

    const fetch = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (statusFilter) params.status = statusFilter;
            const res = await axiosInstance.get('/api/finance/inventory/landed-costs', { params });
            if (res.data?.success) setRows(res.data.data || []);
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to load');
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => { fetch(); }, [fetch]);

    useEffect(() => {
        axiosInstance.get('/api/finance/vendors', { params: { per_page: 200 } })
            .then((r) => setVendors(r.data?.data || [])).catch(() => {});
    }, []);

    const loadReceiptLines = async () => {
        // Fetch posted receipts + their lines to allow selection
        try {
            const res = await axiosInstance.get('/api/finance/inventory/receipts',
                { params: { status: 'POSTED', per_page: 50 } });
            if (res.data?.success) {
                // For each receipt, fetch its lines
                const receipts = res.data.data || [];
                const detailed = await Promise.all(receipts.map((r) =>
                    axiosInstance.get(`/api/finance/inventory/receipts/${r.id}`).then((d) => d.data?.data)
                ));
                setReceipts(detailed.filter(Boolean));
            }
        } catch (e) {
            // non-fatal
        }
    };

    const openDetail = async (bill) => {
        try {
            const res = await axiosInstance.get(`/api/finance/inventory/landed-costs/${bill.id}`);
            if (res.data?.success) setDetail(res.data.data);
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to load');
        }
    };

    const startAllocation = async (bill) => {
        await loadReceiptLines();
        setDetail(bill);
        setAllocate({ open: true, receipt_line_ids: [] });
    };

    const handleCreate = async () => {
        if (!form.amount || Number(form.amount) <= 0) {
            alert('Amount must be > 0');
            return;
        }
        setSaving(true);
        try {
            await axiosInstance.post('/api/finance/inventory/landed-costs', form);
            setCreateOpen(false);
            setForm({
                reference: '', cost_type: 'FREIGHT', vendor_id: null,
                bill_date: new Date().toISOString().slice(0, 10),
                amount: 0, currency: 'USD', allocation_basis: 'VALUE', notes: '',
            });
            fetch();
        } catch (e) {
            alert(e.response?.data?.message || 'Create failed');
        } finally {
            setSaving(false);
        }
    };

    const handleAllocate = async () => {
        if (allocate.receipt_line_ids.length === 0) {
            alert('Select at least one receipt line');
            return;
        }
        try {
            await axiosInstance.post(
                `/api/finance/inventory/landed-costs/${detail.id}/allocate`,
                { receipt_line_ids: allocate.receipt_line_ids },
            );
            setAllocate({ open: false, receipt_line_ids: [] });
            setDetail(null);
            fetch();
        } catch (e) {
            alert(e.response?.data?.message || 'Allocation failed');
        }
    };

    const chipStyle = (s) => {
        const c = STATUS_COLORS[s] || { bg: '#e5e7eb', color: '#374151' };
        return { backgroundColor: c.bg, color: c.color };
    };

    const allReceiptLines = receipts.flatMap((r) =>
        (r.lines || []).map((ln) => ({
            ...ln, receipt_number: r.receipt_number, receipt_id: r.id,
        })));

    return (
        <Box sx={{ p: 3, fontFamily: FONT_FAMILY, bgcolor: '#f8fafc', minHeight: '100vh' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box>
                    <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '1.65rem', fontWeight: 700, color: '#0f172a' }}>
                        Landed Costs
                    </Typography>
                    <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.9rem', color: '#64748b', mt: 0.5 }}>
                        Allocate freight, duty, insurance, and handling to received inventory
                    </Typography>
                </Box>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}
                    sx={{ fontFamily: FONT_FAMILY, textTransform: 'none',
                          bgcolor: INV_ACCENT, '&:hover': { bgcolor: INV_ACCENT_DARK } }}>
                    New Landed Cost
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            <Paper sx={{ p: 2, mb: 2, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Status</InputLabel>
                    <Select value={statusFilter} label="Status"
                        onChange={(e) => setStatusFilter(e.target.value)}>
                        <MenuItem value="">All</MenuItem>
                        {Object.keys(STATUS_COLORS).map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                    </Select>
                </FormControl>
            </Paper>

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Reference</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Type</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Vendor</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Bill Date</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Amount</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Basis</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Status</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? Array.from({ length: 4 }).map((_, i) => (
                            <TableRow key={i}><TableCell colSpan={8}><Skeleton height={32} /></TableCell></TableRow>
                        )) : rows.length === 0 ? (
                            <TableRow><TableCell colSpan={8} sx={{ py: 6, textAlign: 'center', color: '#64748b' }}>
                                No landed cost bills yet
                            </TableCell></TableRow>
                        ) : rows.map((b) => (
                            <TableRow key={b.id} hover>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>{b.reference}</TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY }}>
                                    <Chip size="small" label={b.cost_type?.replace('_', ' ')}
                                        sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} />
                                </TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.85rem' }}>{b.vendor_name || '—'}</TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.85rem' }}>{formatDate(b.bill_date)}</TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">
                                    {formatCurrency(b.amount, b.currency)}
                                </TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.85rem' }}>{b.allocation_basis}</TableCell>
                                <TableCell>
                                    <Chip size="small" label={b.status} style={chipStyle(b.status)}
                                        sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, fontSize: '0.7rem' }} />
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton size="small" onClick={() => openDetail(b)}><VisibilityIcon fontSize="small" /></IconButton>
                                    {b.status === 'DRAFT' && (
                                        <Button size="small" onClick={() => startAllocation(b)}
                                            sx={{ fontFamily: FONT_FAMILY, textTransform: 'none', color: INV_ACCENT }}>
                                            Allocate
                                        </Button>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>

            {/* Create */}
            <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily: FONT_FAMILY }}>New Landed Cost Bill</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2} sx={{ mt: 0 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField size="small" fullWidth label="Reference"
                                helperText="Invoice number from broker/freight"
                                value={form.reference}
                                onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Cost Type</InputLabel>
                                <Select value={form.cost_type} label="Cost Type"
                                    onChange={(e) => setForm((f) => ({ ...f, cost_type: e.target.value }))}>
                                    {COST_TYPES.map((t) => <MenuItem key={t} value={t}>{t.replace('_', ' ')}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <Autocomplete size="small" options={vendors}
                                getOptionLabel={(o) => o.display_name || o.company_name || o.vendor_number || ''}
                                value={vendors.find((v) => v.id === form.vendor_id) || null}
                                onChange={(e, v) => setForm((f) => ({ ...f, vendor_id: v?.id || null }))}
                                renderInput={(params) => <TextField {...params} label="Vendor (optional)" />} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField size="small" fullWidth type="date" label="Bill Date"
                                value={form.bill_date}
                                onChange={(e) => setForm((f) => ({ ...f, bill_date: e.target.value }))}
                                InputLabelProps={{ shrink: true }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField size="small" fullWidth type="number" label="Amount"
                                value={form.amount}
                                onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Allocation Basis</InputLabel>
                                <Select value={form.allocation_basis} label="Allocation Basis"
                                    onChange={(e) => setForm((f) => ({ ...f, allocation_basis: e.target.value }))}>
                                    {BASES.map((b) => <MenuItem key={b} value={b}>{b}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField size="small" fullWidth multiline rows={2} label="Notes"
                                value={form.notes}
                                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleCreate} disabled={saving}
                        sx={{ bgcolor: INV_ACCENT, '&:hover': { bgcolor: INV_ACCENT_DARK } }}>
                        {saving ? 'Saving…' : 'Create Draft'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Detail */}
            <Dialog open={!!detail && !allocate.open} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
                {detail && (
                    <>
                        <DialogTitle sx={{ fontFamily: FONT_FAMILY }}>
                            {detail.reference} · {detail.cost_type?.replace('_', ' ')}
                            <Chip size="small" label={detail.status} style={chipStyle(detail.status)} sx={{ ml: 2 }} />
                        </DialogTitle>
                        <DialogContent dividers>
                            <Stack spacing={1} sx={{ mb: 2 }}>
                                <Typography sx={{ fontFamily: FONT_FAMILY }}>
                                    Amount: <b>{formatCurrency(detail.amount, detail.currency)}</b>
                                </Typography>
                                <Typography sx={{ fontFamily: FONT_FAMILY }}>
                                    Basis: {detail.allocation_basis}
                                </Typography>
                            </Stack>
                            <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, mb: 1 }}>Allocations</Typography>
                            {(detail.allocations || []).length === 0 ? (
                                <Typography sx={{ fontFamily: FONT_FAMILY, color: '#64748b' }}>
                                    Not allocated yet.
                                </Typography>
                            ) : (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Receipt Line</TableCell>
                                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Basis Value</TableCell>
                                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">%</TableCell>
                                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Allocated</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {detail.allocations.map((a) => (
                                            <TableRow key={a.id}>
                                                <TableCell sx={{ fontFamily: FONT_FAMILY }}>#{a.receipt_line_id}</TableCell>
                                                <TableCell sx={{ fontFamily: FONT_FAMILY }} align="right">{a.allocation_basis_value}</TableCell>
                                                <TableCell sx={{ fontFamily: FONT_FAMILY }} align="right">{(Number(a.allocation_percentage) * 100).toFixed(2)}%</TableCell>
                                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">
                                                    {formatCurrency(a.allocated_amount)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setDetail(null)}>Close</Button>
                            {detail.status === 'DRAFT' && (
                                <Button variant="contained" onClick={() => startAllocation(detail)}
                                    sx={{ bgcolor: INV_ACCENT, '&:hover': { bgcolor: INV_ACCENT_DARK } }}>
                                    Allocate to Receipts
                                </Button>
                            )}
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* Allocate */}
            <Dialog open={allocate.open} onClose={() => setAllocate({ open: false, receipt_line_ids: [] })} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontFamily: FONT_FAMILY }}>Select Receipt Lines to Allocate Across</DialogTitle>
                <DialogContent dividers>
                    {allReceiptLines.length === 0 ? (
                        <Typography sx={{ fontFamily: FONT_FAMILY, color: '#64748b', textAlign: 'center', py: 4 }}>
                            No posted receipts found. Post a receipt before allocating landed cost.
                        </Typography>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell />
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Receipt</TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>SKU</TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Qty</TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Unit Cost</TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Value</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {allReceiptLines.map((ln) => (
                                    <TableRow key={ln.id} hover>
                                        <TableCell>
                                            <Checkbox
                                                checked={allocate.receipt_line_ids.includes(ln.id)}
                                                onChange={(e) => {
                                                    setAllocate((a) => {
                                                        const next = new Set(a.receipt_line_ids);
                                                        if (e.target.checked) next.add(ln.id); else next.delete(ln.id);
                                                        return { ...a, receipt_line_ids: Array.from(next) };
                                                    });
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.82rem' }}>{ln.receipt_number}</TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.82rem' }}>{ln.product_sku}</TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY }} align="right">{ln.received_qty}</TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY }} align="right">{formatCurrency(ln.unit_cost)}</TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">
                                            {formatCurrency(Number(ln.received_qty) * Number(ln.unit_cost))}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAllocate({ open: false, receipt_line_ids: [] })}>Cancel</Button>
                    <Button variant="contained" onClick={handleAllocate} disabled={allocate.receipt_line_ids.length === 0}
                        sx={{ bgcolor: INV_ACCENT, '&:hover': { bgcolor: INV_ACCENT_DARK } }}>
                        Allocate ({allocate.receipt_line_ids.length})
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
